import React from 'react';
import styled from 'styled-components';
import { TableItem, ChairItem, StageItem, WallItem, DecorItem, BarItem, BuffetItem, CentrePoleItem, DanceFloorItem, FloorPlanItem } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Conversion factor for feet to pixels (1 foot = 10 pixels)
const FEET_TO_PIXELS = 10;

// Modern color scheme matching App.tsx
const COLORS = {
  primary: '#fbf2ed', // Header color
  secondary: '#d0d7c9', // Button color
  accent: '#d0d7c9', // Accent color (using same as buttons)
  light: '#f8f9fa', // Light background
  dark: '#555555', // Dark text
  lightText: '#555555', // Updated for readability on light backgrounds
  border: '#dee2e6',
  hover: '#c2c9bc', // Darker button color for hover states
  success: '#28a745',
  gray: '#6c757d',
  lightGray: '#e9ecef',
};

interface ToolbarProps {
  onAddItem: (item: FloorPlanItem) => void;
}

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.light};
  border-right: 1px solid ${COLORS.border};
  padding: 20px 16px;
  width: 260px;
  height: 100%;
  overflow-y: auto;
  box-shadow: 1px 0 5px rgba(0, 0, 0, 0.05);
`;

const ToolbarTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.primary};
  padding-bottom: 12px;
  border-bottom: 2px solid ${COLORS.border};
`;

const ToolbarSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.dark};
  display: flex;
  align-items: center;
  
  &::after {
    content: '';
    display: block;
    height: 1px;
    background-color: ${COLORS.border};
    flex-grow: 1;
    margin-left: 8px;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const ItemButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 12px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  color: #555555;

  &:hover {
    background-color: ${COLORS.light};
    border-color: ${COLORS.secondary};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const ItemIcon = styled.div<{ bgColor: string }>`
  width: 48px;
  height: 48px;
  background-color: ${props => props.bgColor};
  border-radius: 6px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ItemLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.dark};
  margin-bottom: 2px;
`;

const ItemDescription = styled.span`
  font-size: 11px;
  color: ${COLORS.gray};
  text-align: center;
`;

const Toolbar: React.FC<ToolbarProps> = ({ onAddItem }) => {
  // Add round table (5ft diameter with chairs around it)
  const handleAddRoundTable = () => {
    const diameter = 5 * FEET_TO_PIXELS; // 5ft = 50px
    const newTable: TableItem = {
      id: uuidv4(),
      type: 'table',
      shape: 'circle',
      seats: 8, // 8 chairs around the 5ft round table
      position: { x: 200, y: 200 },
      size: { width: diameter, height: diameter },
      rotation: 0,
      label: '5ft Round'
    };
    onAddItem(newTable);
  };

  // Add large rectangular table (8ft x 2.5ft with 4 chairs on each long side)
  const handleAddLargeRectTable = () => {
    const tableWidth = 8 * FEET_TO_PIXELS; // 8ft = 80px
    const tableHeight = 2.5 * FEET_TO_PIXELS; // 2.5ft (30 inches) = 25px
    const newTable: TableItem = {
      id: uuidv4(),
      type: 'table',
      shape: 'rectangle',
      seats: 8, // 4 chairs on each long side
      position: { x: 200, y: 200 },
      size: { width: tableWidth, height: tableHeight },
      rotation: 0,
      label: '8ft x 2.5ft'
    };
    onAddItem(newTable);
  };

  // Add small rectangular table (6ft x 2.5ft with 3 chairs on each long side)
  const handleAddSmallRectTable = () => {
    const tableWidth = 6 * FEET_TO_PIXELS; // 6ft = 60px
    const tableHeight = 2.5 * FEET_TO_PIXELS; // 2.5ft (30 inches) = 25px
    const newTable: TableItem = {
      id: uuidv4(),
      type: 'table',
      shape: 'rectangle',
      seats: 6, // 3 chairs on each long side
      position: { x: 200, y: 200 },
      size: { width: tableWidth, height: tableHeight },
      rotation: 0,
      label: '6ft x 2.5ft'
    };
    onAddItem(newTable);
  };

  // Add cruiser table (30 inches diameter with no chairs)
  const handleAddCruiserTable = () => {
    const diameter = 2.5 * FEET_TO_PIXELS; // 2.5ft (30 inches) = 25px
    const newTable: TableItem = {
      id: uuidv4(),
      type: 'table',
      shape: 'circle',
      seats: 0, // No chairs
      position: { x: 200, y: 200 },
      size: { width: diameter, height: diameter },
      rotation: 0,
      label: 'Cruiser'
    };
    onAddItem(newTable);
  };

  // Add head table (8ft x 2.5ft with 4 chairs only on one side)
  const handleAddHeadTable = () => {
    const tableWidth = 8 * FEET_TO_PIXELS; // 8ft = 80px
    const tableHeight = 2.5 * FEET_TO_PIXELS; // 2.5ft (30 inches) = 25px
    const newTable: TableItem = {
      id: uuidv4(),
      type: 'table',
      shape: 'rectangle',
      seats: 4, // 4 chairs on one side only
      position: { x: 200, y: 200 },
      size: { width: tableWidth, height: tableHeight },
      rotation: 0,
      label: 'Head table - 8ft'
    };
    onAddItem(newTable);
  };

  // Add small head table (6ft x 2.5ft with 3 chairs only on one side)
  const handleAddSmallHeadTable = () => {
    const tableWidth = 6 * FEET_TO_PIXELS; // 6ft = 60px
    const tableHeight = 2.5 * FEET_TO_PIXELS; // 2.5ft (30 inches) = 25px
    const newTable: TableItem = {
      id: uuidv4(),
      type: 'table',
      shape: 'rectangle',
      seats: 3, // 3 chairs on one side only
      position: { x: 200, y: 200 },
      size: { width: tableWidth, height: tableHeight },
      rotation: 0,
      label: 'Head table - 6ft'
    };
    onAddItem(newTable);
  };

  const handleAddChair = () => {
    const chairSize = 1.5 * FEET_TO_PIXELS; // 1.5ft = 15px
    const newChair: ChairItem = {
      id: uuidv4(),
      type: 'chair',
      position: { x: 200, y: 200 },
      size: { width: chairSize, height: chairSize },
      rotation: 0
    };
    onAddItem(newChair);
  };

  const handleAddStage = () => {
    const newStage: StageItem = {
      id: uuidv4(),
      type: 'stage',
      position: { x: 400, y: 100 },
      size: { width: 15 * FEET_TO_PIXELS, height: 8 * FEET_TO_PIXELS }, // 15ft x 8ft stage
      rotation: 0
    };
    onAddItem(newStage);
  };

  const handleAddBar = () => {
    const barWidth = 8 * FEET_TO_PIXELS; // 8ft = 80px
    const barHeight = 2.5 * FEET_TO_PIXELS; // 2.5ft (30 inches) = 25px
    const newBar: BarItem = {
      id: uuidv4(),
      type: 'bar',
      position: { x: 300, y: 300 },
      size: { width: barWidth, height: barHeight },
      rotation: 0,
      label: 'Bar'
    };
    onAddItem(newBar);
  };

  const handleAddBuffet = () => {
    const buffetWidth = 6 * FEET_TO_PIXELS; // 6ft = 60px
    const buffetHeight = 2.5 * FEET_TO_PIXELS; // 2.5ft = 25px
    const newBuffet: BuffetItem = {
      id: uuidv4(),
      type: 'buffet',
      position: { x: 300, y: 300 },
      size: { width: buffetWidth, height: buffetHeight },
      rotation: 0,
      label: 'Buffet'
    };
    onAddItem(newBuffet);
  };

  const handleAddDanceFloor = () => {
    const danceFloorWidth = 20 * FEET_TO_PIXELS; // 20ft = 200px
    const danceFloorHeight = 15 * FEET_TO_PIXELS; // 15ft = 150px
    const newDanceFloor: DanceFloorItem = {
      id: uuidv4(),
      type: 'danceFloor',
      position: { x: 300, y: 300 },
      size: { width: danceFloorWidth, height: danceFloorHeight },
      rotation: 0,
      label: 'Dance Floor'
    };
    onAddItem(newDanceFloor);
  };

  const handleAddCentrePole = () => {
    const diameter = 1 * FEET_TO_PIXELS; // 1ft = 10px
    const newCentrePole: CentrePoleItem = {
      id: uuidv4(),
      type: 'centrePole',
      position: { x: 300, y: 300 },
      size: { width: diameter, height: diameter },
      rotation: 0
    };
    onAddItem(newCentrePole);
  };

  const handleAddWall = () => {
    const newWall: WallItem = {
      id: uuidv4(),
      type: 'wall',
      position: { x: 300, y: 200 },
      size: { width: 10 * FEET_TO_PIXELS, height: 0.5 * FEET_TO_PIXELS }, // 10ft x 0.5ft wall
      rotation: 0
    };
    onAddItem(newWall);
  };

  const handleAddDecor = () => {
    const decorSize = 3 * FEET_TO_PIXELS; // 3ft = 30px
    const newDecor: DecorItem = {
      id: uuidv4(),
      type: 'decor',
      decorType: 'generic',
      position: { x: 200, y: 200 },
      size: { width: decorSize, height: decorSize },
      rotation: 0
    };
    onAddItem(newDecor);
  };

  return (
    <ToolbarContainer>
      <ToolbarTitle>Add Items</ToolbarTitle>
      
      <ToolbarSection>
        <SectionTitle>Tables</SectionTitle>
        <ItemsGrid>
          <ItemButton onClick={handleAddRoundTable}>
            <ItemIcon bgColor="#e9ecef">⚫</ItemIcon>
            <ItemLabel>Round Table</ItemLabel>
            <ItemDescription>5ft diameter, 8 chairs</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddLargeRectTable}>
            <ItemIcon bgColor="#e9ecef">▭</ItemIcon>
            <ItemLabel>Large Table</ItemLabel>
            <ItemDescription>8ft × 30in, 8 chairs</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddSmallRectTable}>
            <ItemIcon bgColor="#e9ecef">▭</ItemIcon>
            <ItemLabel>Small Table</ItemLabel>
            <ItemDescription>6ft × 30in, 6 chairs</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddCruiserTable}>
            <ItemIcon bgColor="#e9ecef">⚫</ItemIcon>
            <ItemLabel>Cruiser Table</ItemLabel>
            <ItemDescription>30in diameter, no chairs</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddHeadTable}>
            <ItemIcon bgColor="#e9ecef">▭</ItemIcon>
            <ItemLabel>Head Table</ItemLabel>
            <ItemDescription>8ft × 30in, 4 chairs one side</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddSmallHeadTable}>
            <ItemIcon bgColor="#e9ecef">▭</ItemIcon>
            <ItemLabel>Head Table</ItemLabel>
            <ItemDescription>6ft × 30in, 3 chairs one side</ItemDescription>
          </ItemButton>
        </ItemsGrid>
      </ToolbarSection>

      <ToolbarSection>
        <SectionTitle>Seating</SectionTitle>
        <ItemsGrid>
          <ItemButton onClick={handleAddChair}>
            <ItemIcon bgColor="#f8f9fa">🪑</ItemIcon>
            <ItemLabel>Chair</ItemLabel>
          </ItemButton>
        </ItemsGrid>
      </ToolbarSection>

      <ToolbarSection>
        <SectionTitle>Venue</SectionTitle>
        <ItemsGrid>
          <ItemButton onClick={handleAddStage}>
            <ItemIcon bgColor="#d8cee4">🎭</ItemIcon>
            <ItemLabel>Stage</ItemLabel>
          </ItemButton>
          <ItemButton onClick={handleAddBar}>
            <ItemIcon bgColor="#e0d2c3">🍸</ItemIcon>
            <ItemLabel>Bar</ItemLabel>
            <ItemDescription>8ft × 30in</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddBuffet}>
            <ItemIcon bgColor="#d3e0d1">🍽️</ItemIcon>
            <ItemLabel>Buffet</ItemLabel>
            <ItemDescription>6ft × 30in</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddDanceFloor}>
            <ItemIcon bgColor="#c7b8e0">💃</ItemIcon>
            <ItemLabel>Dance Floor</ItemLabel>
            <ItemDescription>20ft × 15ft</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddCentrePole}>
            <ItemIcon bgColor="#e6e6e6">⊙</ItemIcon>
            <ItemLabel>Centre Pole</ItemLabel>
            <ItemDescription>Tent Only (1ft)</ItemDescription>
          </ItemButton>
          <ItemButton onClick={handleAddWall}>
            <ItemIcon bgColor="#dee2e6">▬</ItemIcon>
            <ItemLabel>Wall</ItemLabel>
          </ItemButton>
        </ItemsGrid>
      </ToolbarSection>

      <ToolbarSection>
        <SectionTitle>Decorations</SectionTitle>
        <ItemsGrid>
          <ItemButton onClick={handleAddDecor}>
            <ItemIcon bgColor="#ffecef">🎨</ItemIcon>
            <ItemLabel>Decoration</ItemLabel>
          </ItemButton>
        </ItemsGrid>
      </ToolbarSection>
    </ToolbarContainer>
  );
};

export default Toolbar; 